import React, { useEffect, useState } from "react";
import { Card, Upload, Button, Select, Tag, Space, message, Typography, Modal, Form, Input, InputNumber, Grid, Tooltip, Collapse, Divider } from "antd";
import Alert from "../../components/GxAlert";
import { InboxOutlined, CheckOutlined, ReloadOutlined, EditOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  scImportUpload, scImportSessions, scImportSession, scImportRowPatch, scImportApprove, downloadTemplate,
  scGetParties,
} from "../../api/client";
import { fmtKg, fmtRial } from "./scConstants";
import { JalaliDateInput, toJalali, toJalaliDateTime } from "../../utils/dateUtils";
import PartyPicker from "./PartyPicker";
import { useAuth } from "../../context/AuthContext";
import GxTable from "../../components/GxTable";
import GxTabs from "../../components/GxTabs";

const { Text } = Typography;

const ROW_TYPE = {
  yarn_purchase: { l: "خرید نخ", c: "blue" },
  dyed_to_warehouse: { l: "حواله → انبار قرچک", c: "green" },
  raw_received_at_pooshan: { l: "امانی → دریافتِ خام", c: "geekblue" },
  payment: { l: "پرداخت", c: "green" },
  check: { l: "چک", c: "gold" },
  opening_balance: { l: "مانده اول دوره", c: "default" },
  norbakhsh_output: { l: "خروجی بافت (نوربخش)", c: "geekblue" },
  raw_to_dyeing: { l: "ارسال به رنگرزی", c: "cyan" },
  dyed_from_dyeing: { l: "خروجی رنگرزی", c: "purple" },
  waste: { l: "ضایعات", c: "volcano" },
  adjustment: { l: "اصلاحیه", c: "gold" },
  unknown: { l: "نامشخص", c: "red" },
};
const ROW_STATUS = {
  ready_to_approve: { l: "آمادهٔ تأیید", c: "green" },
  needs_review: { l: "نیازمند بررسی", c: "orange" },
  duplicate_warning: { l: "قبلاً ثبت‌شده", c: "volcano" },
  invalid: { l: "نامعتبر", c: "red" },
  approved: { l: "تأییدشده", c: "blue" },
  rejected: { l: "ردشده", c: "default" },
};
const STATUS_OPTS = [
  { value: "ready_to_approve", label: "آمادهٔ تأیید" },
  { value: "needs_review", label: "نیازمند بررسی" },
  { value: "rejected", label: "ردشده" },
];
// چهار قالبِ ایمپورت — هر کدام فایلِ بومیِ شرکت یا قالبِ Excelِ استانداردِ ما را می‌پذیرد
// `partyType` = نقشِ شرکتِ صاحبِ فایل؛ شرکتِ دیگری از همان نقش (نخِ غیرِ درین، رنگرزیِ دوم) هم انتخاب‌پذیر است
const SC_TEMPLATES = [
  { value: "universal", label: "همگانی (همهٔ بخش‌ها / هر شرکت) — Excel/CSV/PDF", dl: "sc-universal", file: "gharchak-import-universal.xlsx", accept: ".xlsx,.xls,.csv,.pdf", partyType: null },
  { value: "derin_statement", label: "خرید نخ — قالبِ درین‌ریس (Excel/PDF/CSV)", dl: "sc-derin", file: "gharchak-import-derin.xlsx", accept: ".pdf,.xlsx,.xls,.csv", partyType: "supplier" },
  { value: "norbakhsh_statement", label: "بافندگی — قالبِ نوربخش", dl: "sc-norbakhsh", file: "gharchak-import-norbakhsh.xlsx", accept: ".pdf,.xlsx,.xls,.csv", partyType: "knitting_workshop" },
  { value: "pooshan_statement", label: "رنگرزی — قالبِ پوشان", dl: "sc-pooshan", file: "gharchak-import-pooshan.xlsx", accept: ".pdf,.xlsx,.xls,.csv", partyType: "dyeing_provider" },
  // صورتحسابِ رنگ و تکمیلِ نیکان (PDF) — طرف‌حسابِ پیش‌فرض با نام («نیکان»)، نه پوشان
  { value: "nikan_statement", label: "رنگرزی — قالبِ نیکان (PDF)", dl: "sc-universal", file: "gharchak-import-universal.xlsx", accept: ".pdf", partyType: "dyeing_provider", partyName: "نیکان" },
];
const tplOf = (v) => SC_TEMPLATES.find((t) => t.value === v) || {};
/** بازهٔ تاریخِ فاکتورهای داخلِ فایل */
const invoiceRange = (s) => {
  if (!s?.invoice_date_from) return "—";
  const a = toJalali(s.invoice_date_from); const b = toJalali(s.invoice_date_to);
  return a === b ? a : `${a} تا ${b}`;
};

export default function ImportCenterPage() {
  const { can } = useAuth();
  const isMobile = !Grid.useBreakpoint().md;
  const [tab, setTab] = useState("upload");
  const [template, setTemplate] = useState("universal");
  const [partyId, setPartyId] = useState(null);      // شرکتِ صاحبِ فایل (خالی = پیش‌فرضِ قالب)
  const [parties, setParties] = useState([]);
  const [uploading, setUploading] = useState(false);
  /* v2.171.0 (کاربر): «چند فایل با هم آپلود شود و همه پشتِ هم با یک جداکنندهٔ ساده نمایش داده شوند».
     `batch` = فایل‌های همین دور (هر کدام یک جلسه با ردیف‌هایش، یا خطای همان فایل). تکی هم همین است. */
  const [batch, setBatch] = useState([]);           // [{...session} | {file_name, error}]
  const [selectedBy, setSelectedBy] = useState({}); // sessionId → [rowIds]
  const [progress, setProgress] = useState(null);   // «۲ از ۵»
  const [sessions, setSessions] = useState([]);
  const [approving, setApproving] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editDirty, setEditDirty] = useState(false);
  const [editForm] = Form.useForm();

  const loadSessions = async () => {
    try { const r = await scImportSessions(); setSessions(r.data); } catch { /* */ }
  };
  useEffect(() => {
    loadSessions();
    scGetParties().then((r) => setParties(r.data)).catch(() => {});
  }, []);

  const readyIds = (sess) => (sess.rows || []).filter((x) => x.status === "ready_to_approve").map((x) => x.id);

  /* antd برای هر فایل یک‌بار `beforeUpload(file, fileList)` صدا می‌زند ⇒ فقط با فایلِ اولِ فهرست کلِ
     دسته را به‌ترتیب می‌خوانیم (موازی نه: تشخیصِ «تکراری» به فایل‌های قبلیِ همین دسته هم نیاز دارد). */
  const doUpload = async (file, fileList) => {
    const files = fileList && fileList.length ? fileList : [file];
    if (file !== files[0]) return false;
    setUploading(true);
    const out = [];
    const sel = {};
    for (let i = 0; i < files.length; i++) {
      setProgress(`${i + 1} از ${files.length}`);
      try {
        const r = await scImportUpload(files[i], template, partyId);
        out.push(r.data);
        sel[r.data.id] = readyIds(r.data);
      } catch (e) {
        out.push({ file_name: files[i].name, error: e?.response?.data?.detail || "خطا در خواندن فایل" });
      }
    }
    setBatch(out);
    setSelectedBy(sel);
    const ok = out.filter((x) => !x.error);
    const rows = ok.reduce((a, x) => a + (x.total_rows || 0), 0);
    const dups = ok.reduce((a, x) => a + (x.rows || []).filter((r) => r.status === "duplicate_warning").length, 0);
    const bad = out.length - ok.length;
    const msg = `${files.length} فایل، ${rows} ردیف خوانده شد` + (dups ? ` — ${dups} ردیفِ تکراری کنار گذاشته شد` : "")
      + (bad ? ` — ${bad} فایل خوانده نشد` : "");
    (bad || dups ? message.warning : message.success)(msg, 6);
    setProgress(null);
    setUploading(false);
    setTab("preview");
    loadSessions();
    return false;
  };

  const reloadSession = async (id) => {
    const r = await scImportSession(id);
    setBatch((b) => b.map((x) => (x.id === id ? r.data : x)));
    return r.data;
  };

  const openSession = async (id) => {
    try {
      const r = await scImportSession(id);
      setBatch([r.data]);
      setSelectedBy({ [id]: readyIds(r.data) });
      setTab("preview");
    } catch { message.error("خطا در بارگذاری جلسه"); }
  };

  const changeRowStatus = async (sid, rowId, status) => {
    try {
      await scImportRowPatch(rowId, { status });
      const s2 = await reloadSession(sid);
      // ردیفی که «ثبتِ مجدد» شد خودکار انتخاب شود؛ ردشده از انتخاب بیرون برود
      setSelectedBy((m) => {
        const cur = new Set(m[sid] || []);
        if (status === "ready_to_approve") cur.add(rowId); else cur.delete(rowId);
        const valid = new Set((s2.rows || []).map((x) => x.id));
        return { ...m, [sid]: [...cur].filter((x) => valid.has(x)) };
      });
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };
  const openEdit = (r, sid) => {
    setEditRow({ ...r, _sid: sid });
    setEditDirty(false);
    editForm.setFieldsValue({
      row_type: r.row_type, event_date: r.event_date,
      external_serial: r.external_serial, order_code: r.order_code,
      permit_number: r.permit_number,
      raw_description: r.raw_description,
      fabric_type: r.fabric_type, color: r.color,
      weight_kg: r.weight_kg, unit_price: r.unit_price, total_amount: r.total_amount,
      carton_count: r.carton_count, pallet_count: r.pallet_count, roll_count: r.roll_count,
      yarn_type: r.yarn_type, note: r.note,
    });
  };
  const closeEdit = () => {
    if (editDirty) {
      Modal.confirm({
        title: "تغییرات ذخیره نشده",
        content: "تغییراتی در فرم دارید که ذخیره نشده‌اند. بدون ذخیره خارج می‌شوید؟",
        okText: "خروج بدون ذخیره", cancelText: "برگرد",
        okButtonProps: { danger: true },
        onOk: () => { setEditDirty(false); setEditRow(null); },
      });
    } else {
      setEditRow(null);
    }
  };
  const saveEdit = async (andClose = true) => {
    const v = await editForm.validateFields();
    try {
      await scImportRowPatch(editRow.id, v);
      message.success("ردیف ویرایش شد");
      setEditDirty(false);
      if (andClose) setEditRow(null);
      reloadSession(editRow._sid);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در ویرایش"); }
  };

  /** تأییدِ ردیف‌های انتخاب‌شده — یک فایل (`sid`) یا همهٔ فایل‌های این دور (`sid` خالی). */
  const approveSelected = async (sid) => {
    const targets = batch.filter((x) => !x.error && (sid == null || x.id === sid)
      && (selectedBy[x.id] || []).length);
    if (!targets.length) { message.warning("حداقل یک ردیف انتخاب کنید"); return; }
    setApproving(sid ?? "all");
    let approved = 0, created = 0;
    for (const t of targets) {
      try {
        const r = await scImportApprove(t.id, selectedBy[t.id]);
        approved += r.data.approved || 0; created += r.data.events_created || 0;
        const s2 = await reloadSession(t.id);
        setSelectedBy((m) => ({ ...m, [t.id]: readyIds(s2) }));
      } catch (e) { message.error(`${t.file_name}: ${e?.response?.data?.detail || "خطا در تأیید"}`); }
    }
    if (approved) message.success(`${approved} ردیف تأیید شد (${created} رویدادِ پیش‌نویس ساخته شد)`);
    loadSessions();
    setApproving(false);
  };

  const rowCols = [
    { title: "ردیف", dataIndex: "row_number", width: 60, align: "center" },
    { title: "نوع", dataIndex: "row_type", width: 110,
      render: (v) => <Tag color={(ROW_TYPE[v] || {}).c}>{(ROW_TYPE[v] || {}).l || v}</Tag> },
    // تاریخِ خودِ فاکتور (از داخلِ فایل) — جدا از تاریخِ ایمپورت که بالای کارت است
    { title: "تاریخ فاکتور", dataIndex: "event_date", width: 110,
      render: (v, r) => r.jalali_date || (v ? toJalali(v) : "—") },
    { title: "شماره فاکتور", dataIndex: "external_serial", width: 100, align: "center", render: (v) => v || "—" },
    { title: "شماره سفارش", dataIndex: "order_code", width: 100, align: "center", render: (v) => v || "—" },
    { title: "شرح", dataIndex: "raw_description", ellipsis: true },
    { title: "کالا", dataIndex: "fabric_type", width: 150, ellipsis: true, render: (v) => v || "—" },
    { title: "رنگ", dataIndex: "color", width: 80, align: "center", render: (v) => v || "—" },
    { title: "وزن", dataIndex: "weight_kg", width: 110, align: "center", render: (v) => v ? fmtKg(v) : "—" },
    { title: "فی", dataIndex: "unit_price", width: 110, align: "center", render: (v) => v ? fmtRial(v) : "—" },
    { title: "مبلغ/بدهکار", dataIndex: "total_amount", width: 130, align: "center",
      render: (v, r) => fmtRial(v ?? r.debit) },
    { title: "بستانکار", dataIndex: "credit", width: 120, align: "center", render: (v) => v ? fmtRial(v) : "—" },
    { title: "مجوز", dataIndex: "permit_number", width: 80, render: (v) => v || "—" },
    { title: "کارتن", dataIndex: "carton_count", width: 70, align: "center", render: (v) => v ?? "—" },
    { title: "طاقه", dataIndex: "roll_count", width: 70, align: "center", render: (v) => v ?? "—" },
    { title: "وضعیت", dataIndex: "status", width: 175,
      render: (v, r) => {
        if (r.status === "approved")
          return <Tag color="blue">تأییدشده{r.created_event_id ? " (رویداد ساخته شد)" : ""}</Tag>;
        if (r.status === "duplicate_warning")
          return (
            <Space size={4} wrap>
              <Tooltip title={r.note || "این ردیف قبلاً ثبت شده یا این فایل قبلاً آپلود شده است"}>
                <Tag color="volcano" style={{ margin: 0, cursor: "help" }}>⚠ تکراری</Tag>
              </Tooltip>
              <Button size="small" type="link" style={{ padding: 0, height: "auto" }}
                onClick={() => changeRowStatus(r._sid, r.id, "ready_to_approve")}>ثبتِ مجدد</Button>
              <Button size="small" type="link" danger style={{ padding: 0, height: "auto" }}
                onClick={() => changeRowStatus(r._sid, r.id, "rejected")}>رد</Button>
            </Space>
          );
        return <Select size="small" value={v} style={{ width: 140 }} options={STATUS_OPTS}
          onChange={(s) => changeRowStatus(r._sid, r.id, s)} />;
      } },
    { title: "ویرایش", width: 70, fixed: "right",
      render: (_, r) => r.status !== "approved" && can("sc.event.create") && (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r, r._sid)} />
      ) },
  ];

  const mobileHide = ["row_number", "raw_description", "unit_price", "total_amount", "credit", "permit_number", "carton_count", "order_code", "fabric_type", "color"];
  const sessionBlock = (sess) => {
    const dups = (sess.rows || []).filter((x) => x.status === "duplicate_warning").length;
    const sel = selectedBy[sess.id] || [];
    return (
      <Card size="small"
        title={<Space wrap>
          <span>{sess.file_name}</span>
          <Tag>{sess.total_rows} ردیف</Tag>
          <Tag color="blue">{sess.approved_rows} تأییدشده</Tag>
          {sess.party_name && <Tag color="purple">طرف حساب: {sess.party_name}</Tag>}
          <Tag color="geekblue">تاریخ ایمپورت: {toJalaliDateTime(sess.created_at) || "—"}</Tag>
          <Tag color="green">تاریخ فاکتور: {invoiceRange(sess)}</Tag>
        </Space>}
        extra={can("sc.event.create") && (
          <Button type="primary" icon={<CheckOutlined />} loading={approving === sess.id}
            disabled={!sel.length} onClick={() => approveSelected(sess.id)}>
            تأیید {sel.length} ردیفِ این فایل
          </Button>
        )}
      >
        {dups > 0 && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }}
            message={`⚠ ${dups} ردیفِ تکراری شناسایی شد`}
            description="این ردیف‌ها قبلاً ثبت شده‌اند یا این فایل قبلاً آپلود شده — به‌صورتِ پیش‌فرض انتخاب نشده‌اند. اگر مطمئنید، در ستونِ «وضعیت» روی «ثبتِ مجدد» بزنید." />
        )}
        <GxTable
          layoutId="sc-import-rows" rowKey="id" size="small"
          dataSource={(sess.rows || []).map((r) => ({ ...r, _sid: sess.id }))}
          columns={isMobile ? rowCols.filter((c) => !mobileHide.includes(c.dataIndex)) : rowCols}
          pagination={{ pageSize: 50 }} scroll={{ x: "max-content" }}
          rowSelection={{
            selectedRowKeys: sel,
            onChange: (keys) => setSelectedBy((m) => ({ ...m, [sess.id]: keys })),
            getCheckboxProps: (r) => ({ disabled: r.status === "approved" || r.status === "rejected" }),
          }}
        />
      </Card>
    );
  };

  const totalSel = batch.reduce((a, x) => a + (x.error ? 0 : (selectedBy[x.id] || []).length), 0);
  const previewTab = !batch.length ? (
    <Alert type="info" showIcon message="ابتدا از تب «آپلود فایل» یک یا چند فایل بارگذاری کنید یا از «تاریخچه» یک جلسه را باز کنید." />
  ) : (
    <div>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
        message="ایمپورت مستقیماً رویدادِ نهایی نمی‌سازد. ردیف‌ها پس از تأیید، فاکتورِ پیش‌نویس می‌سازند که در «فاکتورها» تأیید نهایی می‌شوند. پرداخت/چک/مانده فعلاً فقط ثبت می‌شوند." />
      {batch.length > 1 && can("sc.event.create") && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <Text strong>{batch.length} فایل</Text>
          <Button type="primary" icon={<CheckOutlined />} loading={approving === "all"} disabled={!totalSel}
            onClick={() => approveSelected()}>
            تأییدِ همهٔ ردیف‌های انتخاب‌شده ({totalSel}) در همهٔ فایل‌ها
          </Button>
        </div>
      )}
      {batch.map((sess, i) => (
        <div key={sess.id || `err-${i}`}>
          {/* جداکنندهٔ ساده بینِ فایل‌ها */}
          {batch.length > 1 && (
            <Divider orientation="right" style={{ margin: i ? "20px 0 10px" : "0 0 10px" }}>
              فایلِ {i + 1} از {batch.length}
            </Divider>
          )}
          {sess.error ? (
            <Alert type="error" showIcon message={sess.file_name} description={sess.error} />
          ) : sessionBlock(sess)}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>ایمپورت فایل — زنجیره تأمین</h2>
      <GxTabs activeKey={tab} onChange={setTab} items={[
        {
          key: "upload", label: "آپلود فایل",
          children: (
            <Card style={{ maxWidth: 560 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ display: "block", marginBottom: 6 }}>بخش / قالبِ فایل:</Text>
                <Select value={template} style={{ width: "100%" }}
                  onChange={(v) => {
                    setTemplate(v);
                    // قالبِ شرکت‌دار (نیکان) ⇒ همان شرکت از پیش انتخاب شود
                    const nm = tplOf(v).partyName;
                    const hit = nm && parties.find((p) => p.is_active !== false
                      && String(p.name).replace(/ي/g, "ی").replace(/ك/g, "ک").includes(nm));
                    setPartyId(hit ? hit.id : null);
                  }}
                  options={SC_TEMPLATES.map((t) => ({ value: t.value, label: t.label }))} />
                <Text strong style={{ display: "block", margin: "12px 0 6px" }}>شرکت / طرف حسابِ این فایل:</Text>
                <PartyPicker size="middle" value={partyId} onChange={setPartyId} parties={parties}
                  types={tplOf(template).partyType ? [tplOf(template).partyType] : null}
                  defaultHint={!!tplOf(template).partyType}
                  placeholder="اختیاری — از خودِ فایل/پیش‌فرض"
                  onCreated={(p) => setParties((ps) => [...ps, p])} />
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                  اگر فاکتور از شرکتِ دیگری است (مثلاً نخ از غیرِ درین)، همین‌جا انتخاب یا اضافه‌اش کنید؛ فاکتورها به نامِ همان شرکت ثبت می‌شوند.
                </Text>
                <Button icon={<DownloadOutlined />} block style={{ marginTop: 8 }}
                  onClick={() => {
                    const t = SC_TEMPLATES.find((x) => x.value === template);
                    downloadTemplate(t.dl, t.file).catch(() => message.error("خطا در دانلودِ نمونه"));
                  }}>
                  دانلودِ فایلِ نمونهٔ این بخش
                </Button>
                <Alert type="info" showIcon style={{ marginTop: 8 }}
                  message="هر شرکت می‌تواند فایلِ بومیِ خودش را بدهد، یا فایلِ نمونهٔ بالا را پر کند. در «همگانی» ستونِ «نوع حرکت» مشخص می‌کند هر سطر مربوط به کدام مرحله است." />
              </div>
              <Upload.Dragger accept={(SC_TEMPLATES.find((x) => x.value === template) || {}).accept || ".xlsx,.xls,.pdf"}
                multiple beforeUpload={doUpload} showUploadList={false} disabled={uploading}>
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">{uploading ? `در حالِ خواندنِ فایلِ ${progress || ""}…` : "یک یا چند فایل را اینجا بکشید یا کلیک کنید"}</p>
                <p className="ant-upload-hint">می‌شود چند فایل را با هم انتخاب کرد؛ همه خوانده و پشتِ هم برای بررسی نمایش داده می‌شوند (هیچ رکوردی مستقیم ثبت نمی‌شود).</p>
              </Upload.Dragger>
            </Card>
          ),
        },
        { key: "preview", label: "پیش‌نمایش و اصلاح ردیف‌ها", children: previewTab },
        {
          key: "history", label: "تاریخچهٔ ایمپورت‌ها",
          children: (
            <Card extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadSessions}>تازه‌سازی</Button>}>
              <GxTable rowKey="id" size="small" dataSource={sessions} pagination={{ pageSize: 20 }}
                layoutId="sc-import-history" scroll={{ x: "max-content" }}
                columns={[
                  { title: "فایل", dataIndex: "file_name", width: 200, ellipsis: true },
                  { title: "تاریخ ایمپورت", dataIndex: "created_at", width: 165,
                    sorter: (a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")),
                    render: (v) => toJalaliDateTime(v) || "—" },
                  { title: "تاریخ فاکتور", dataIndex: "invoice_date_from", width: 170,
                    sorter: (a, b) => String(a.invoice_date_from || "").localeCompare(String(b.invoice_date_from || "")),
                    render: (_, r) => invoiceRange(r) },
                  { title: "بخش/قالب", dataIndex: "template", width: 170,
                    render: (v) => tplOf(v).label || v },
                  { title: "طرف حساب", dataIndex: "party_name", width: 130, render: (v) => v || "—" },
                  { title: "توسط", dataIndex: "created_by", width: 100, render: (v) => v || "—" },
                  { title: "ردیف‌ها", dataIndex: "total_rows", width: 90, align: "center" },
                  { title: "تأییدشده", dataIndex: "approved_rows", width: 90, align: "center" },
                  { title: "وضعیت", dataIndex: "status", width: 110,
                    render: (v) => <Tag>{{ draft: "پیش‌نویس", partial: "ناقص", completed: "کامل" }[v] || v}</Tag> },
                  { title: "", width: 90, render: (_, r) => <Button size="small" onClick={() => openSession(r.id)}>باز کردن</Button> },
                ]} />
            </Card>
          ),
        },
      ]} />

      {/* ویرایشِ ردیفِ ایمپورت‌شده (اصلاحِ خطاهای خواندنِ خودکار) */}
      <Modal title={`ویرایش ردیف ${editRow?.row_number || ""}`} open={!!editRow}
        onCancel={closeEdit} width={620}
        footer={[
          <Button key="cancel" onClick={closeEdit}>انصراف</Button>,
          <Button key="save" type="primary" onClick={() => saveEdit(true)}>ذخیره</Button>,
        ]}>
        {editRow?.status === "needs_review" && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="این ردیف نیازمند بررسی است — فیلدهای خالی یا نامطمئن را تکمیل کنید." />
        )}
        <Form form={editForm} layout="vertical" style={{ direction: "rtl" }}
          onValuesChange={() => setEditDirty(true)}>
          <Space wrap>
            <Form.Item name="row_type" label="نوع ردیف" style={{ minWidth: 200 }}>
              <Select options={Object.entries(ROW_TYPE).map(([v, m]) => ({ value: v, label: m.l }))} />
            </Form.Item>
            <Form.Item name="event_date" label="تاریخ"><JalaliDateInput /></Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="external_serial" label="شماره فاکتور (سری)"><Input style={{ width: 140 }} /></Form.Item>
            <Form.Item name="order_code" label="شماره سفارش"><Input style={{ width: 140 }} /></Form.Item>
            <Form.Item name="permit_number" label="شماره مجوز"><Input style={{ width: 140 }} /></Form.Item>
          </Space>
          <Form.Item name="raw_description" label="توضیحات / شرح کامل">
            <Input.TextArea rows={2} autoSize={{ minRows: 2, maxRows: 5 }} />
          </Form.Item>
          <Space wrap>
            <Form.Item name="fabric_type" label="نوع کالا / پارچه"><Input style={{ width: 240 }} /></Form.Item>
            <Form.Item name="color" label="رنگ"><Input style={{ width: 120 }} /></Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="weight_kg" label="وزن (کیلوگرم)"><InputNumber min={0} style={{ width: 140 }} /></Form.Item>
            <Form.Item name="unit_price" label="فی (ریال)"><InputNumber min={0} style={{ width: 150 }}
              formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} /></Form.Item>
            <Form.Item name="total_amount" label="مبلغ (ریال)"><InputNumber min={0} style={{ width: 170 }}
              formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} /></Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="roll_count" label="تعداد طاقه"><InputNumber min={0} style={{ width: 110 }} /></Form.Item>
            <Form.Item name="carton_count" label="تعداد کارتن"><InputNumber min={0} style={{ width: 110 }} /></Form.Item>
            <Form.Item name="pallet_count" label="تعداد پالت"><InputNumber min={0} style={{ width: 110 }} /></Form.Item>
          </Space>
          <Form.Item name="yarn_type" label="نوع نخ"><Input /></Form.Item>
          <Form.Item name="note" label="یادداشت"><Input.TextArea rows={2} /></Form.Item>
        </Form>
        {editRow && (
          <Collapse ghost size="small" items={[{
            key: "raw",
            label: "جزئیات استخراج (دادهٔ خام فایل)",
            children: (
              <div style={{ fontSize: 12, lineHeight: 1.9 }}>
                <div><Text type="secondary">شرح ذخیره‌شده:</Text> {editRow.raw_description || "—"}</div>
                <div><Text type="secondary">یادداشت پارسر:</Text> {editRow.note || "—"}</div>
                <div><Text type="secondary">تاریخ شمسی فایل:</Text> {editRow.jalali_date || "—"}</div>
                <div><Text type="secondary">بدهکار/بستانکار/مانده:</Text> {[editRow.debit, editRow.credit, editRow.balance].map((x) => x ?? "—").join(" / ")}</div>
                <div><Text type="secondary">وضعیت:</Text> {(ROW_STATUS[editRow.status] || {}).l || editRow.status}</div>
              </div>
            ),
          }]} />
        )}
      </Modal>
    </div>
  );
}
